import { useMemo } from "react";

import { ThemeProvider } from "styled-components";

import { Stack } from "@similarweb/ui-components/foundations";

import ChatMessage, {
    type ChatMessageProps,
} from "app/pages/sales-intelligence/modules/ai-agent/components/ChatMessage/ChatMessage";
import { VersionMessage } from "app/pages/sales-intelligence/modules/ai-agent/components/ChatMessage/VersionMessage";
import { CHAT_THEME } from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/styles";
import type { FeedbackReaction, ThreadMessage } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";

import FlatMessagesList from "./FlatMessagesList";
import GroupedMessagesList from "./GroupedMessagesList";
import type { ChatRenderMode, BaseMessagesListProps, RenderMessageComponent } from "./types";

export type MessagesListProps = BaseMessagesListProps & {
    onFeedback?: (runId: string, reaction: FeedbackReaction) => void;
    renderLoaderCondition?: boolean;
    isStreamingLoaderVisible?: boolean;
    onVersionMessageClick?: (id: string) => void;
} & Pick<ChatMessageProps, "isLoading" | "mdComponentProps" | "withControls" | "onCopy">;

const MessagesList = ({
    uiContext,
    messages,
    groupBy,
    isLoading,
    lastUserMessageRef,
    lastAiMessageRef,
    withControls,
    onFeedback,
    onVersionMessageClick,
    renderLoaderCondition,
    isStreamingLoaderVisible,
    ...msgProps
}: MessagesListProps) => {
    const renderMode: ChatRenderMode = uiContext.renderMode ?? "flat";
    const theme = CHAT_THEME[uiContext.chatTheme];

    const [lastUserMessageIndex, lastAiMessageIndex, lastVersionMessageIndex] = useMemo(() => {
        return [
            messages.findLastIndex(({ type }) => type === "human"),
            messages.findLastIndex(({ type }) => type === "ai"),
            messages.findLastIndex(({ type }) => type === "version"),
        ];
    }, [messages]);

    const renderMessage: RenderMessageComponent = (
        message: ThreadMessage,
        index: number,
        isLastInSequence: boolean,
    ) => {
        if (message.type === "human") {
            return (
                <ChatMessage
                    key={index}
                    variant="human"
                    content={message.content}
                    {...(index === lastUserMessageIndex ? { messageRef: lastUserMessageRef } : {})}
                />
            );
        }

        if (message.type === "ai") {
            return (
                <ChatMessage
                    key={message.id}
                    variant="ai"
                    content={message.content}
                    reaction={message.reaction}
                    isLoading={!!(isLoading && isLastInSequence)}
                    {...(index === lastAiMessageIndex ? { messageRef: lastAiMessageRef } : {})}
                    messageId={message.id}
                    mdComponentProps={msgProps.mdComponentProps}
                    markdownComponent={uiContext.markdownComponent}
                    loader={uiContext.loader}
                    withControls={withControls}
                    onFeedback={(reaction) => onFeedback?.(message.id, reaction)}
                    onCopy={msgProps.onCopy}
                />
            );
        }

        if (message.type === "version") {
            return (
                <VersionMessage
                    key={index}
                    index={Number(message.id)}
                    message={message}
                    onClick={onVersionMessageClick}
                    isCurrent={index === lastVersionMessageIndex}
                    isDisabled={message.isDisabled}
                    {...(index === lastVersionMessageIndex ? { messageRef: lastAiMessageRef } : {})}
                />
            );
        }

        return null;
    };

    return (
        <ThemeProvider theme={theme}>
            <Stack
                gap={theme.msgBlockGapY}
                {...(theme.msgContainerMinWidth ? { minWidth: theme.msgContainerMinWidth } : {})}
            >
                {renderMode === "flat" ? (
                    <FlatMessagesList
                        messages={messages}
                        renderMessage={renderMessage}
                        uiContext={uiContext}
                        isLoading={renderLoaderCondition ?? isLoading}
                        lastUserMessageRef={lastUserMessageRef}
                        lastAiMessageRef={lastAiMessageRef}
                        isStreamingLoaderVisible={isStreamingLoaderVisible}
                    />
                ) : (
                    <GroupedMessagesList
                        messages={messages}
                        groupBy={groupBy}
                        renderMessage={renderMessage}
                        uiContext={uiContext}
                        isLoading={isLoading}
                        theme={theme}
                        lastUserMessageRef={lastUserMessageRef}
                        lastAiMessageRef={lastAiMessageRef}
                    />
                )}
            </Stack>
        </ThemeProvider>
    );
};

export default MessagesList;
